'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function NorolojiDoktorlariBotoksPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/noroloji-doktorlari-neden-botoks-onermez';
  const pageTitle = 'Nöroloji Doktorları Neden Botoks Önermez? - Sinir-Kas Etkileri ve Riskler';
  const pageDescription = 'Nöroloji uzmanları botoksu neden her hastaya önermez? Sinir-kas etkileri, kontrendikasyonları, riskleri ve bilimsel nedenleriyle tam açıklama.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Nöroloji Doktorları Neden Botoks Önermez?', href: '/tr/blog/noroloji-doktorlari-neden-botoks-onermez' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Nöroloji uzmanları botoks uygulaması değerlendirmesi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-red-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Nöroloji & Botoks
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Nöroloji Doktorları Neden Botoks Önermez?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>20 Ocak 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>12 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Botoks (botulinum toksini), estetik uygulamalarda kırışıklıkları azaltmak için yaygın biçimde kullanılan bir yöntemdir. Ancak nöroloji hekimleri bu tedaviyi her hasta için önermemektedir. Bunun nedeni yalnızca estetik değil — sinir sistemine olan etkiler, bireysel risk profili ve tıbbi durumlardır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Sinir-Kas İletişimini Bastırıcı Etki
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks, sinir uçlarından kaslara giden iletiyi geçici olarak engelleyerek kasların kasılmasını durdurur. Bu etki, estetik amaçla fayda sağlarken bazı hastalarda zararlı olabilir.
              </p>
              
              <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-6">
                <h3 className="text-lg font-semibold text-red-900 mb-3">Özellikle şu durumlarda:</h3>
                <ul className="list-disc list-inside text-red-800 space-y-2">
                  <li>Miyastenia gravis, Lambert-Eaton sendromu gibi nöromüsküler iletim bozuklukları</li>
                  <li>Motor nöron hastalıkları</li>
                </ul>
                <p className="text-red-800 mt-3">
                  Bu hastalarda kas zayıflığı artabilir, solunum kasları veya yutma refleksi olumsuz etkilenebilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Sistemik Yayılma ve Komplikasyon Riski
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks normalde lokal etki gösterir; fakat beklenmedik durumlarda toksin etkisi enjeksiyon bölgesinin dışına yayılabilir. Bu durumda ciddi komplikasyonlar ortaya çıkabilir:
              </p>

              <div className="bg-orange-50 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-orange-900 mb-3">Potansiyel Komplikasyonlar:</h3>
                <ul className="list-disc list-inside text-orange-800 space-y-2">
                  <li>Yutma güçlüğü</li>
                  <li>Konuşma bozuklukları</li>
                  <li>Göz kapağı düşüklüğü (ptozis)</li>
                  <li>Solunum kaslarının etkilenmesi, nefes darlığı</li>
                </ul>
                <p className="text-orange-800 mt-3 font-medium">
                  Bu risk özellikle doz yüksekse ya da uygulama tekniği yanlışsa artar.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Diğer Kontrendikasyonlar ve Tıbbi Engeller
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Nöroloji uzmanları şu durumlarda botoks uygulamasından kaçınabilir:
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-blue-900 mb-3">Tıbbi Kontrendikasyonlar:</h3>
                  <ul className="list-disc list-inside text-blue-800 space-y-2">
                    <li>Enjeksiyon bölgesinde aktif enfeksiyon veya iltihap</li>
                    <li>Gebelik ve emzirme dönemi</li>
                    <li>Botoks içeriklerine karşı alerji / duyarlılık</li>
                    <li>Var olan kas ya da sinir sorunları</li>
                    <li>Kullanılan ilaçlarla etkileşim riski</li>
                  </ul>
                </div>

                <div className="bg-yellow-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-yellow-900 mb-3">Nörolojik Risk Faktörleri:</h3>
                  <ul className="list-disc list-inside text-yellow-800 space-y-2">
                    <li>Epilepsi hastalığı</li>
                    <li>Multiple skleroz (MS)</li>
                    <li>Parkinson hastalığı</li>
                    <li>Migren (bazı durumlarda)</li>
                    <li>Kas hastalıkları</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Yan Etkiler ve Beklenen Reaksiyonlar
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks uygulamasından sonra sık görülen etkiler:
              </p>

              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">Sık Görülen Yan Etkiler:</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Enjeksiyon bölgesinde şişlik, kızarıklık, morluk</li>
                  <li>Baş ağrısı, gerginlik hissi</li>
                  <li>Geçici kas zayıflığı</li>
                  <li>Nadiren asimetri, geçici ptozis</li>
                </ul>
                <p className="text-gray-700 mt-3">
                  Bu etkiler genellikle hafif ve geçicidir; ancak risk faktörlü hastalarda ciddi tablo da oluşabilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Uygun Hasta Seçimi ve Karar Süreci
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Nörologlar botoks uygulamadan önce hastanın sağlık geçmişini dikkatle inceler:
              </p>

              <div className="space-y-4 mb-8">
                <div className="bg-green-50 rounded-lg p-6">
                  <h3 className="font-semibold text-green-900 mb-2">Değerlendirme Kriterleri:</h3>
                  <ul className="list-disc list-inside text-green-800 space-y-1">
                    <li>Nöromüsküler hastalık var mı?</li>
                    <li>Hangi ilaçlar kullanılıyor?</li>
                    <li>Kas zayıflığı, solunum sorunu, yutma problemi var mı?</li>
                    <li>Risk-fayda analizi yapılır, hasta ile bilgilendirme yapılır</li>
                  </ul>
                </div>
                <p className="text-gray-700 font-medium">
                  Ancak uygun hasta seçildiğinde, botoks güvenli ve etkili olabilir.
                </p>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Nöroloji uzmanları botoks değerlendirmesi"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Botoks kalıcı mıdır?</h3>
                  <p className="text-gray-700">Hayır — etkisi geçicidir, genellikle 4-6 ay sürer.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Etki süresi kişiye göre değişir mi?</h3>
                  <p className="text-gray-700">Evet — metabolizma, kas yapısı, mimik alışkanlıkları gibi faktörler etkiler.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. İlk uygulamada doz daha mı düşük olur?</h3>
                  <p className="text-gray-700">Evet, genellikle yavaş başlanır, sonucuna göre ayarlanır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Kaç yaşında başlanmalı?</h3>
                  <p className="text-gray-700">25–30 yaş civarından itibaren, mimik çizgileri belirginleşmeye başlayınca düşünülebilir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. Hamilelikte uygulanabilir mi?</h3>
                  <p className="text-gray-700">Hayır — gebelik ve emzirme döneminde güvenilir veri yoktur, önerilmez.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">6. Yan etkiler nelerdir?</h3>
                  <p className="text-gray-700">Şişlik, morarma, geçici zayıflık, nadiren geçici göz kapağı düşüklüğü.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>American Academy of Dermatology (AAD) — Botulinum Toxin Kullanım Kılavuzları</li>
                <li>Türkiye Estetik Plastik Cerrahi Derneği (TEPCD) — Botulinum Toksin Rehberi</li>
                <li>Mayo Clinic — "Botulinum Toxin: Uses and Risks"</li>
                <li>FDA — Botulinum Toxin Type A Onay Belgeleri</li>
                <li>International Society of Aesthetic Plastic Surgery (ISAPS) — Küresel Estetik İstatistikler</li>
              </ul>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Botoks Uygulaması İçin Uzman Değerlendirme</h3>
              <p className="mb-6">Nörolojik durumunuzu değerlendirerek size uygun botoks tedavisi planlayalım. Uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Botoks uygulaması için nörolojik değerlendirme hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-blue-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Uzman Değerlendirme
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/botoks-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Nedir? Nasıl Yapılır?</h4>
              <p className="text-gray-600 text-sm">Botoks uygulaması hakkında temel bilgiler ve uygulama süreci.</p>
            </Link>
            <Link
              href="/tr/blog/botoks-yan-etkileri"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Yan Etkileri</h4>
              <p className="text-gray-600 text-sm">Botoks uygulamasının olası yan etkileri ve dikkat edilmesi gerekenler.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
